import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  Globe,
  Inbox,
  Loader2,
  MinusCircle,
  Plus,
  RefreshCw,
  Search,
  Trash2,
  XCircle
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

interface SyncResult {
  url: string;
  status: 'success' | 'failed' | 'skipped';
  message?: string;
  mode?: string;
  document_count?: number;
}

interface SubPage {
  url: string;
  count: number;
  indexed_date: string;
}

interface DomainGroup {
  base_domain: string;
  total_documents: number;
  indexed_date: string;
  sub_pages: SubPage[];
}

const AdminSiteSync = () => {
  const { t, i18n } = useTranslation();
  const [urls, setUrls] = useState('');
  const [syncDialogOpen, setSyncDialogOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncResults, setSyncResults] = useState<SyncResult[]>([]);
  const [showResults, setShowResults] = useState(false);
  const [domains, setDomains] = useState<Record<string, DomainGroup>>({});
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedDomains, setExpandedDomains] = useState<Set<string>>(new Set());
  
  // Delete dialogs
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteDomainDialogOpen, setDeleteDomainDialogOpen] = useState(false);
  const [urlToDelete, setUrlToDelete] = useState<string | null>(null);
  const [domainToDelete, setDomainToDelete] = useState<string | null>(null);

  const fetchGroupedUrls = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/grouped-urls', {
        method: 'POST',
        headers: {
          'Cache-Control': 'no-cache'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setDomains(data.domains || {});
    } catch (error) {
      console.error('Error fetching grouped URLs:', error);
      toast.error('Failed to fetch indexed sites');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGroupedUrls();
  }, []);

  const toggleDomain = (domain: string) => {
    const newExpanded = new Set(expandedDomains);
    if (newExpanded.has(domain)) {
      newExpanded.delete(domain);
    } else {
      newExpanded.add(domain);
    }
    setExpandedDomains(newExpanded);
  };

  const handleDeleteUrlClick = (url: string) => {
    setUrlToDelete(url);
    setDeleteDialogOpen(true);
  };

  const handleDeleteDomainClick = (domain: string) => {
    setDomainToDelete(domain);
    setDeleteDomainDialogOpen(true);
  };

  const handleDeleteUrlConfirm = async () => {
    if (!urlToDelete) return;

    try {
      const response = await fetch('/api/delete-url', {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ url: urlToDelete }),
      });

      if (!response.ok) {
        throw new Error('Delete failed');
      }

      const result = await response.json();
      toast.success(result.message || 'URL deleted successfully');
      await fetchGroupedUrls();
    } catch (error) {
      console.error('Error deleting URL:', error);
      toast.error('Failed to delete URL');
    } finally {
      setDeleteDialogOpen(false);
      setUrlToDelete(null);
    }
  };

  const handleDeleteDomainConfirm = async () => {
    if (!domainToDelete) return;

    try {
      const response = await fetch('/api/delete-domain', {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ domain: domainToDelete }),
      });

      if (!response.ok) {
        throw new Error('Delete failed');
      }

      const result = await response.json();
      toast.success(result.message || 'Domain deleted successfully');
      await fetchGroupedUrls();
    } catch (error) {
      console.error('Error deleting domain:', error);
      toast.error('Failed to delete domain');
    } finally {
      setDeleteDomainDialogOpen(false);
      setDomainToDelete(null);
    }
  };

  const normalizeUrl = (url: string): string => {
    // Remove quotes (single or double) and trim whitespace
    return url.trim().replace(/^["']|["']$/g, '').trim();
  };

  const parsedUrls = urls
    .split(/[\n,]+/)
    .map((url) => normalizeUrl(url))
    .filter(Boolean);

  const formatDate = (value: string) => {
    if (!value) return '—';
    try {
      return new Date(value).toLocaleDateString(
        i18n.language.toLowerCase().startsWith('sr') ? 'sr-RS' : 'en-GB',
        { day: '2-digit', month: 'short', year: 'numeric' }
      );
    } catch {
      return value;
    }
  };

  const openSyncDialog = () => {
    setSyncResults([]);
    setShowResults(false);
    setSyncDialogOpen(true);
  };

  const handleSync = async () => {
    // Parse URLs from textarea - support both newlines and commas
    // Also normalize URLs (remove quotes, etc.)
    const urlList = parsedUrls;

    if (urlList.length === 0) {
      toast.error('Please enter at least one URL');
      return;
    }

    const invalidUrl = urlList.find((url) => {
      try {
        const parsed = new URL(url);
        return parsed.protocol !== 'http:' && parsed.protocol !== 'https:';
      } catch {
        return true;
      }
    });
    if (invalidUrl) {
      toast.error(
        t('admin.siteSync.enterUrls.invalid', 'Enter complete http:// or https:// URLs')
      );
      return;
    }

    try {
      setSyncing(true);
      setShowResults(false);
      setSyncResults([]);

      toast.info(`Scraping ${urlList.length} URL(s)...`);

      const response = await fetch('/api/site-sync', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          urls: urlList,
          mode: 'scrape'
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.results) {
        setSyncResults(data.results);
        setShowResults(true);
        
        const successCount = data.results.filter((r: SyncResult) => r.status === 'success').length;
        const failCount = data.results.filter((r: SyncResult) => r.status === 'failed').length;
        const skippedCount = data.results.filter((r: SyncResult) => r.status === 'skipped').length;
        
        if (successCount > 0) {
          toast.success(`Sync completed: ${successCount} successful, ${failCount} failed, ${skippedCount} skipped`);
        } else if (skippedCount > 0) {
          toast.info(`All URLs already indexed: ${skippedCount} skipped`);
        } else {
          toast.error(`Sync failed: ${failCount} failed`);
        }
        
        // Refresh the grouped URLs list
        await fetchGroupedUrls();
      } else {
        toast.success(data.message || 'Site sync completed successfully');
      }
    } catch (error) {
      console.error('Error syncing sites:', error);
      toast.error('Failed to sync sites. Please try again.');
    } finally {
      setSyncing(false);
    }
  };

  // Filter domains based on search query
  const filteredDomains = Object.entries(domains).filter(([domain, data]) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    
    // Search in domain name
    if (domain.toLowerCase().includes(query)) return true;
    
    // Search in sub-pages
    return data.sub_pages.some(page => page.url.toLowerCase().includes(query));
  });

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            {t('admin.siteSync.title', 'Site Sync')}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {t(
              'admin.siteSync.description',
              'Manage web pages indexed in the FONGPT knowledge base.'
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="icon"
            onClick={fetchGroupedUrls}
            disabled={loading}
            aria-label={t('admin.siteSync.refresh', 'Refresh sites')}
            title={t('admin.siteSync.refresh', 'Refresh sites')}
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          <Button onClick={openSyncDialog}>
            <Plus className="mr-2 h-4 w-4" />
            {t('admin.siteSync.addPages', 'Index URLs')}
          </Button>
        </div>
      </div>

      <section className="overflow-hidden rounded-xl border border-border bg-card">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-5 py-4">
          <p className="text-sm text-muted-foreground">
            {t('admin.siteSync.siteCount', {
              count: filteredDomains.length,
              defaultValue: `${filteredDomains.length} sites`
            })}
          </p>
          <div className="relative w-full sm:w-72">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder={t(
                'admin.siteSync.indexedSites.searchPlaceholder',
                'Search domains or URLs…'
              )}
              aria-label={t('admin.siteSync.search', 'Search indexed sites')}
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              className="h-9 pl-9"
            />
          </div>
        </div>

        {loading ? (
          <div className="space-y-2 p-5">
            {Array.from({ length: 5 }).map((_, index) => (
              <Skeleton key={index} className="h-16 w-full" />
            ))}
          </div>
        ) : filteredDomains.length === 0 ? (
          <div className="flex min-h-64 flex-col items-center justify-center px-6 text-center">
            <Inbox className="mb-3 h-8 w-8 text-muted-foreground/60" />
            <p className="font-medium">
              {searchQuery
                ? t('admin.siteSync.indexedSites.noResults', 'No sites match your search')
                : t('admin.siteSync.indexedSites.noSites', 'No indexed sites')}
            </p>
            <p className="mt-1 text-sm text-muted-foreground">
              {searchQuery
                ? t('admin.siteSync.adjustSearch', 'Try a different search term.')
                : t('admin.siteSync.empty', 'Index a URL to add web content.')}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {filteredDomains.map(([domain, data]) => {
              const expanded = expandedDomains.has(domain) || Boolean(searchQuery);
              const matchingPages = searchQuery
                ? data.sub_pages.filter((page) =>
                    page.url.toLowerCase().includes(searchQuery.toLowerCase())
                  )
                : data.sub_pages;
              const visiblePages = matchingPages.length > 0 ? matchingPages : data.sub_pages;
              return (
                <div key={domain}>
                  <div className="flex items-center pr-5">
                    <button
                      type="button"
                      className="fongpt-hover flex min-w-0 flex-1 items-center gap-3 px-5 py-4 text-left"
                      onClick={() => toggleDomain(domain)}
                      aria-expanded={expanded}
                    >
                      {expanded ? (
                        <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
                      ) : (
                        <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                      )}
                      <Globe className="h-4 w-4 shrink-0 text-muted-foreground" />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium">{domain}</p>
                        <p className="mt-0.5 text-xs text-muted-foreground">
                          {t('admin.siteSync.domainSummary', {
                            pages: data.sub_pages.length,
                            documents: data.total_documents,
                            defaultValue: `${data.sub_pages.length} pages · ${data.total_documents} documents`
                          })}
                        </p>
                      </div>
                      <span className="hidden whitespace-nowrap text-xs text-muted-foreground md:block">
                        {formatDate(data.indexed_date)}
                      </span>
                    </button>
                    <Button
                      type="button"
                      size="icon"
                      variant="destructive"
                      className="h-8 w-8 shrink-0"
                      onClick={() => handleDeleteDomainClick(domain)}
                      aria-label={t('admin.siteSync.deleteSite', 'Delete site')}
                      title={t('admin.siteSync.deleteSite', 'Delete site')}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>

                  {expanded ? (
                    <div className="bg-muted/15 px-5 pb-3 pl-16">
                      {visiblePages.length === 0 ? (
                        <p className="py-3 text-sm text-muted-foreground">
                          {t('admin.siteSync.noPages', 'No pages found')}
                        </p>
                      ) : (
                        <div className="divide-y divide-border/60">
                          {visiblePages.map((page) => (
                            <div key={page.url} className="flex items-center gap-3 py-2.5">
                              <a
                                href={page.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="fongpt-hover-text flex min-w-0 flex-1 items-center gap-2 text-sm"
                              >
                                <span className="truncate">{page.url}</span>
                                <ExternalLink className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                              </a>
                              <span className="hidden whitespace-nowrap text-xs text-muted-foreground sm:block">
                                {t('admin.siteSync.documentCount', {
                                  count: page.count,
                                  defaultValue: `${page.count} documents`
                                })}
                              </span>
                              <span className="hidden whitespace-nowrap text-xs text-muted-foreground lg:block">
                                {formatDate(page.indexed_date)}
                              </span>
                              <Button
                                type="button"
                                size="icon"
                                variant="destructive"
                                className="h-7 w-7 shrink-0"
                                onClick={() => handleDeleteUrlClick(page.url)}
                                aria-label={t('admin.siteSync.deletePage', 'Delete page')}
                                title={t('admin.siteSync.deletePage', 'Delete page')}
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        )}
      </section>

      <Dialog
        open={syncDialogOpen}
        onOpenChange={(open) => {
          if (!syncing) setSyncDialogOpen(open);
        }}
      >
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>
              {t('admin.siteSync.enterUrls.title', 'Index web pages')}
            </DialogTitle>
            <DialogDescription>
              {t(
                'admin.siteSync.enterUrls.description',
                'Add one complete URL per line. Each page is indexed individually.'
              )}
            </DialogDescription>
          </DialogHeader>

          <Textarea
            placeholder={t(
              'admin.siteSync.enterUrls.placeholder',
              'https://fon.bg.ac.rs/page'
            )}
            value={urls}
            onChange={(event) => setUrls(event.target.value)}
            rows={7}
            className="resize-none font-mono text-sm"
            disabled={syncing}
            aria-label={t('admin.siteSync.enterUrls.label', 'URLs to index')}
          />

          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>
              {t('admin.siteSync.enterUrls.onePerLine', 'One URL per line')}
            </span>
            <span className="tabular-nums">
              {t('admin.siteSync.enterUrls.count', {
                count: parsedUrls.length,
                defaultValue: `${parsedUrls.length} URLs`
              })}
            </span>
          </div>

          {syncing ? (
            <div className="flex items-center gap-3 rounded-lg bg-muted/30 px-3 py-2.5 text-sm">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              {t('admin.siteSync.enterUrls.processing', 'Fetching and indexing pages…')}
            </div>
          ) : null}

          {showResults && syncResults.length > 0 ? (
            <div className="max-h-64 space-y-1 overflow-y-auto rounded-lg bg-muted/20 p-2">
              {syncResults.map((result) => (
                <div key={result.url} className="flex items-start gap-3 px-2 py-2">
                  {result.status === 'success' ? (
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[#56c0a9]" />
                  ) : result.status === 'skipped' ? (
                    <MinusCircle className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                  ) : (
                    <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">{result.url}</p>
                    {result.message ? (
                      <p className="mt-0.5 text-xs text-muted-foreground">
                        {result.message}
                      </p>
                    ) : null}
                  </div>
                  {result.document_count ? (
                    <span className="whitespace-nowrap text-xs text-muted-foreground">
                      {t('admin.siteSync.documentCount', {
                        count: result.document_count,
                        defaultValue: `${result.document_count} documents`
                      })}
                    </span>
                  ) : null}
                </div>
              ))}
            </div>
          ) : null}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setSyncDialogOpen(false)}
              disabled={syncing}
            >
              {t('admin.siteSync.enterUrls.cancel', 'Close')}
            </Button>
            <Button onClick={handleSync} disabled={syncing || parsedUrls.length === 0}>
              {syncing ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Globe className="mr-2 h-4 w-4" />
              )}
              {syncing
                ? t('admin.siteSync.enterUrls.scraping', 'Indexing…')
                : t('admin.siteSync.enterUrls.startSync', 'Start indexing')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('admin.siteSync.deleteDialog.title', 'Confirm Deletion')}</DialogTitle>
            <DialogDescription>
              {t('admin.siteSync.deleteDialog.description', 'Are you sure you want to delete all documents from this URL?')}
              <br /><br />
              <strong className="break-all">{urlToDelete}</strong>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              {t('admin.siteSync.deleteDialog.cancel', 'Cancel')}
            </Button>
            <Button variant="destructive" onClick={handleDeleteUrlConfirm}>
              {t('admin.siteSync.deleteDialog.delete', 'Delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Domain Dialog */}
      <Dialog open={deleteDomainDialogOpen} onOpenChange={setDeleteDomainDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('admin.siteSync.deleteDomainDialog.title', 'Delete Entire Site')}</DialogTitle>
            <DialogDescription>
              {t('admin.siteSync.deleteDomainDialog.description', 'This will delete ALL pages and documents from this domain. This action cannot be undone.')}
              <br /><br />
              <strong>{domainToDelete}</strong>
              <br />
              <span className="text-sm">
                {domainToDelete && domains[domainToDelete] && (
                  <>({domains[domainToDelete].sub_pages.length} pages, {domains[domainToDelete].total_documents} documents)</>
                )}
              </span>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDomainDialogOpen(false)}>
              {t('admin.siteSync.deleteDomainDialog.cancel', 'Cancel')}
            </Button>
            <Button variant="destructive" onClick={handleDeleteDomainConfirm}>
              {t('admin.siteSync.deleteDomainDialog.delete', 'Delete All')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminSiteSync;
